/**
 * V2 rendering for tool calls list
 * Shows text blocks with bullets, tools as buttons (no bullets, indented)
 * MCP tools are clickable, Bash is disabled
 */

import * as React from "react";
import { cn } from "src/common/utils";

import { V2ToolButton } from "./V2ToolButton";
import { isFilesystemTool, isToolButtonDisabled, shouldShowTool, shouldShowToolAsButton } from "./v2Utils";

interface V2ToolCallsListProps {
  toolCalls: readonly any[];
  messageContent?: string;
  messageType?: string;
  onToolClick?: (index: number) => void;
  isThinking?: boolean;
  thinking?: string;
  thinkingSummary?: string;
}

export const V2ToolCallsList: React.FC<V2ToolCallsListProps> = React.memo<V2ToolCallsListProps>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function V2ToolCallsList({ toolCalls, messageContent, messageType, onToolClick, isThinking = false, thinking = "", thinkingSummary = "" }) {

    // Check if any toolCalls have text
    const hasAnyToolCallText = toolCalls && toolCalls.length > 0 && toolCalls.some(tc => tc.text && tc.text.trim().length > 0);

    // Only use message.content if no toolCalls have text
    const shouldShowMessageContent = !hasAnyToolCallText && messageContent && messageContent.trim().length > 0;

    if (!toolCalls || toolCalls.length === 0) {
      // Still render if we have message content to show
      if (!shouldShowMessageContent) {
        return null;
      }
    }

    // Blink the messageContent if it's shown and we're thinking
    const shouldMessageContentBlink = shouldShowMessageContent && isThinking && !hasAnyToolCallText;

    // Find the last visible toolCall item
    let lastVisibleIndex = -1;
    for (let i = toolCalls.length - 1; i >= 0; i--) {
      const hasText = toolCalls[i].text && toolCalls[i].text.trim().length > 0;
      const hasTool = toolCalls[i].toolUse;
      const toolName = toolCalls[i].toolUse?.name;

      if (hasText || (hasTool && shouldShowTool(toolName))) {
        lastVisibleIndex = i;
        break;
      }
    }

    // Track if we've rendered any content (to add spacing for subsequent items)
    let hasRenderedContent = shouldShowMessageContent;

    // Group consecutive Filesystem tools together, but start new groups when text blocks appear
    //
    // Rendering order:
    // 1. Top-level thinking summary (if present) - overall agent reasoning summary
    // 2. Tool call "text" with cluster summary indented below
    // 3. Filesystem tool clusters - groups of consecutive Filesystem tools with count
    //
    // Note: Raw thinking is no longer displayed, only thinking summaries at the message level
    const renderItems: Array<{
      type: "text" | "tool" | "filesystem-cluster" | "thinking";
      content: any;
      indices: number[];
      clusterText?: string;
    }> = [];

    // Add top-level thinking summary at the beginning if present
    // Only show thinking summary, not raw thinking
    if (thinkingSummary && thinkingSummary.trim().length > 0) {
      renderItems.push({
        type: "thinking",
        content: thinkingSummary,
        indices: [-1], // Special index for thinking
      });
    }

    let i = 0;
    while (i < toolCalls.length) {
      const toolCall = toolCalls[i];
      const hasText = toolCall.text && toolCall.text.trim().length > 0;
      const hasTool = toolCall.toolUse;
      const toolName = toolCall.toolUse?.name;

      // Text-only block - this breaks any Filesystem clustering
      if (hasText && !hasTool) {
        renderItems.push({
          type: "text",
          content: toolCall,
          indices: [i],
        });
        i++;
        continue;
      }

      // Check if this is the start of a Filesystem tool cluster
      if (hasTool && isFilesystemTool(toolName)) {
        // Store text from the first tool to render with the cluster
        // Note: We don't store thinking anymore as we only show thinking summaries
        const clusterText = hasText ? toolCall.text : undefined;

        const clusterTools = [toolCall];
        const clusterIndices = [i];

        // Look ahead to find consecutive Filesystem tools WITHOUT text
        // Stop clustering when we encounter text so it can be rendered before a new cluster starts
        i++;
        while (i < toolCalls.length) {
          const nextToolCall = toolCalls[i];
          const nextHasText = nextToolCall.text && nextToolCall.text.trim().length > 0;
          const nextHasTool = nextToolCall.toolUse;
          const nextToolName = nextToolCall.toolUse?.name;

          // Stop clustering if we hit a text-only block (no tool)
          if (nextHasText && !nextHasTool) {
            break;
          }

          // Stop clustering if next Filesystem tool has text
          // This allows the text to be rendered before a new cluster starts
          if (nextHasTool && isFilesystemTool(nextToolName)) {
            if (nextHasText) {
              // End current cluster here, next iteration will handle this tool
              break;
            }
            // Continue clustering - no text on this tool
            clusterTools.push(nextToolCall);
            clusterIndices.push(i);
            i++;
          } else {
            break;
          }
        }

        renderItems.push({
          type: "filesystem-cluster",
          content: clusterTools,
          indices: clusterIndices,
          clusterText,
        });
        continue;
      }

      // Regular tool block (MCP tools, etc.)
      if (hasTool) {
        // Note: We no longer render thinking for individual tool calls
        // Only thinking summaries at the message level are shown

        // If the tool has text, render it as a text block
        if (hasText) {
          renderItems.push({
            type: "text",
            content: toolCall,
            indices: [i],
          });
        }
        // Render the tool itself
        renderItems.push({
          type: "tool",
          content: toolCall,
          indices: [i],
        });
        i++;
        continue;
      }

      i++;
    }

    return (
      <div className="flex flex-col gap-0">
        {/* Show message content first ONLY if no toolCalls have text */}
        {shouldShowMessageContent && (
          <div className="flex gap-2 items-start">
            <span
              className={cn(
                "text-muted-foreground flex-shrink-0",
                shouldMessageContentBlink && "animate-pulse"
              )}
            >
              ●
            </span>
            <span className="text-foreground">
              {messageContent.endsWith(":") ? messageContent.slice(0, -1) + "..." : messageContent}
            </span>
          </div>
        )}

        {renderItems.map((item, itemIndex) => {
          const index = item.indices[0];
          const isLatestVisible = item.indices.includes(lastVisibleIndex);
          const shouldBlink = isLatestVisible && isThinking && !shouldShowMessageContent;

          // Thinking message
          if (item.type === "thinking") {
            const thinkingText = item.content as string;
            const isThinkingBlink = isThinking && itemIndex === renderItems.length - 1;

            const element = (
              <div key={`thinking-${itemIndex}`} className={cn("flex gap-2 items-start", hasRenderedContent && "mt-3")}>
                <span
                  className={cn(
                    "text-muted-foreground flex-shrink-0",
                    isThinkingBlink && "animate-pulse"
                  )}
                >
                  ●
                </span>
                <span className="text-foreground">
                  {thinkingText.endsWith(":") ? thinkingText.slice(0, -1) + "..." : thinkingText}
                </span>
              </div>
            );
            hasRenderedContent = true;
            return element;
          }

          // Filesystem tool cluster
          if (item.type === "filesystem-cluster") {
            const clusterTools = item.content as any[];
            const count = clusterTools.length;
            const hasClusterText = item.clusterText && item.clusterText.trim().length > 0;

            const element = (
              <div key={`cluster-${itemIndex}`} className={cn("flex flex-col gap-1", hasRenderedContent && "mt-3")}>
                {/* Note: We don't render thinking anymore, only thinking summaries at the message level */}

                {/* Render text as bullet if present */}
                {hasClusterText && (
                  <div className="flex gap-2 items-start">
                    <span className={cn("text-muted-foreground flex-shrink-0", shouldBlink && "animate-pulse")}>
                      ●
                    </span>
                    <span className="text-foreground">
                      {item.clusterText!.endsWith(":") ? item.clusterText!.slice(0, -1) + "..." : item.clusterText}
                    </span>
                  </div>
                )}

                {/* Render cluster summary indented if we have text */}
                <div className={cn("text-sm text-muted-foreground italic", hasClusterText && "ml-4")}>
                  Running Analysis Tools: {count}
                </div>
              </div>
            );
            hasRenderedContent = true;
            return element;
          }

          // Text-only blocks
          if (item.type === "text") {
            const toolCall = item.content;
            const displayText = toolCall.text.endsWith(":")
              ? toolCall.text.slice(0, -1) + "..."
              : toolCall.text;

            const element = (
              <div key={index} className={cn("flex gap-2 items-start", hasRenderedContent && "mt-3")}>
                <span
                  className={cn(
                    "text-muted-foreground flex-shrink-0",
                    shouldBlink && "animate-pulse"
                  )}
                >
                  ●
                </span>
                <span className="text-foreground">{displayText}</span>
              </div>
            );
            hasRenderedContent = true;
            return element;
          }

          // Regular tool blocks
          if (item.type === "tool") {
            const toolCall = item.content;
            const hasText = toolCall.text && toolCall.text.trim().length > 0;
            const toolName = toolCall.toolUse.name;
            const showAsButton = shouldShowToolAsButton(toolName);

            const needsTopSpacing = hasRenderedContent && (hasText || showAsButton);

            const element = (
              <div key={index} className={cn("flex flex-col gap-1", needsTopSpacing && "mt-2")}>
                {hasText && (
                  <div className="flex gap-2 items-start">
                    <span
                      className={cn(
                        "text-muted-foreground flex-shrink-0",
                        shouldBlink && "animate-pulse"
                      )}
                    >
                      ●
                    </span>
                    <span className="text-foreground">
                      {toolCall.text.endsWith(":") ? toolCall.text.slice(0, -1) + "..." : toolCall.text}
                    </span>
                  </div>
                )}

                {showAsButton && (
                  <div className="ml-4">
                    <V2ToolButton
                      toolCall={toolCall}
                      onClick={() => onToolClick?.(index)}
                      isDisabled={isToolButtonDisabled(toolName)}
                    />
                  </div>
                )}
              </div>
            );

            if (hasText || showAsButton) {
              hasRenderedContent = true;
            }

            return element;
          }

          return null;
        })}
      </div>
    );
  }
);

